import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ProductWithTagNames } from '../types/Product';
import { getProductsWithToken, getAuthenticatedImageUrl } from '../services/productsList';
import { useTags } from './TagsContext';

interface ProductsContextType {
  products: ProductWithTagNames[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  refreshProducts: () => Promise<void>;
  getAuthenticatedImage: (imageUrl: string) => Promise<string>;
  goToPage: (page: number) => void;
}

const ProductsContext = createContext<ProductsContextType | undefined>(undefined);

interface ProductsProviderProps {
  children: ReactNode;
}

export const ProductsProvider: React.FC<ProductsProviderProps> = ({ children }) => {
  const [products, setProducts] = useState<ProductWithTagNames[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(16);
  const [totalItems, setTotalItems] = useState(0);

  const totalPages = Math.ceil(totalItems / pageSize);

  const { tags } = useTags();

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const offset = (currentPage - 1) * pageSize;
      const response = await getProductsWithToken(pageSize, offset, tags.length > 0 ? tags : undefined);
      console.log(response);
      setProducts(response.products);
      setTotalItems(response.total || 0);
      
    } catch (err) {
      setError('Failed to fetch products');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshProducts = async () => {
    await fetchProducts();
  };

  const getAuthenticatedImage = async (imageUrl: string): Promise<string> => {
    try {
      return await getAuthenticatedImageUrl(imageUrl);
    } catch (error) {
      console.error('Error getting authenticated image:', error);
      throw error;
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [currentPage, pageSize]);

  const goToPage = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const value: ProductsContextType = {
    products,
    loading,
    error,
    currentPage,
    pageSize,
    totalPages,
    refreshProducts,
    getAuthenticatedImage,
    goToPage,
  };

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
};

export const useProducts = (): ProductsContextType => {
  const context = useContext(ProductsContext);
  if (context === undefined) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return context;
}; 